import React, { useEffect, useState } from 'react';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';
import {
  fetchVendorAuthActivity,
  type VendorAuthActivity,
  type VendorAuthActivitySession,
} from '../../services/MasterAdmin/vendorService';

const formatDateTime = (dateString: string | null | undefined) => {
  if (dateString == null || dateString === '') return '—';
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

interface VendorAuthActivitySectionProps {
  vendorId: string | undefined;
  /** When false, omit the in-card title (e.g. dedicated login activity page already has a page heading). */
  showSectionTitle?: boolean;
}

const VendorAuthActivitySection: React.FC<VendorAuthActivitySectionProps> = ({
  vendorId,
  showSectionTitle = true,
}) => {
  const [authActivity, setAuthActivity] = useState<VendorAuthActivity | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!vendorId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchVendorAuthActivity(vendorId);
        if (!cancelled && res.success && res.data?.authActivity) {
          setAuthActivity(res.data.authActivity);
        } else if (!cancelled) {
          setError('Could not load login activity');
        }
      } catch {
        if (!cancelled) setError('Failed to load login activity');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [vendorId]);

  if (!vendorId) return null;

  return (
    <div className="mb-8 rounded-xl border border-gray-100 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
      {showSectionTitle && (
        <div className="flex items-center gap-2 mb-5">
          <ShieldCheckIcon className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Login activity</h2>
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
        </div>
      )}

      {!loading && error && (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      {!loading && !error && authActivity && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 p-4">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Current status</p>
              <div className="mt-2">
                <span
                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    authActivity.isLoggedIn
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                      : 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300'
                  }`}
                >
                  {authActivity.isLoggedIn ? 'Logged in' : 'Logged out'}
                </span>
              </div>
            </div>
            <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 p-4">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Active devices</p>
              <p className="mt-2 text-lg font-semibold text-gray-900 dark:text-white">{authActivity.activeSessions ?? 0}</p>
            </div>
            <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 p-4">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">App Version</p>
              <p className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">
                {(() => {
                  const latest = authActivity.recentSessions?.[0];
                  if (!latest?.appVersion) return '—';
                  return `${latest.appVersion}${latest.appBuildNumber ? ` (${latest.appBuildNumber})` : ''}`;
                })()}
              </p>
            </div>
            <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 p-4">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Last login</p>
              <p className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">
                {formatDateTime(authActivity.lastLogin)}
              </p>
            </div>
            <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 p-4">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Last logout</p>
              <p className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">
                {formatDateTime(authActivity.lastLogout)}
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Per-device sessions</h3>
            {(authActivity.recentSessions?.length ?? 0) === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No login or logout sessions found for this vendor.</p>
            ) : (
              <div className="max-h-96 overflow-x-auto overflow-y-auto rounded-lg border border-gray-100 bg-white dark:border-gray-700 dark:bg-gray-900/40 scrollbar-light">
                <table className="min-w-[64rem] w-full text-xs">
                  <thead className="bg-gray-50 dark:bg-gray-800/60 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Platform</th>
                      <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">App Version</th>
                      <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium min-w-[220px]">OS / Device</th>
                      <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Method</th>
                      <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Device ID</th>
                      <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Session ID</th>
                      <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Status</th>
                      <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Login time</th>
                      <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Logout time</th>
                      <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Logout reason</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {(authActivity.recentSessions || []).map((s: VendorAuthActivitySession) => (
                      <tr key={s.sessionId}>
                        <td className="px-4 py-2 text-gray-700 dark:text-gray-300 capitalize">{s.platform || '—'}</td>
                        <td className="px-4 py-2 text-gray-700 dark:text-gray-300 font-medium whitespace-nowrap">
                          {s.appVersion ? `${s.appVersion}${s.appBuildNumber ? ` (${s.appBuildNumber})` : ''}` : '—'}
                        </td>
                        <td className="px-4 py-2 text-gray-700 dark:text-gray-300 min-w-[220px]">
                          {s.osVersion || s.deviceModel ? `${s.osVersion || ''} ${s.deviceModel ? `(${s.deviceModel})` : ''}`.trim() : '—'}
                        </td>
                        <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{s.loginMethod || '—'}</td>
                        <td
                          className="px-4 py-2 text-gray-700 dark:text-gray-300 font-mono max-w-[140px] truncate"
                          title={s.deviceId || ''}
                        >
                          {s.deviceId || '—'}
                        </td>
                        <td
                          className="px-4 py-2 text-gray-700 dark:text-gray-300 font-mono max-w-[160px] truncate"
                          title={s.sessionId}
                        >
                          {s.sessionId}
                        </td>
                        <td className="px-4 py-2">
                          <span
                            className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                              s.status === 'ACTIVE'
                                ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'
                                : s.status === 'LOGGED_OUT'
                                ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-300'
                                : s.status === 'REVOKED'
                                ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                                : 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
                            }`}
                          >
                            {s.status}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                          {formatDateTime(s.loginAt)}
                        </td>
                        <td className="px-4 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                          {formatDateTime(s.logoutAt)}
                        </td>
                        <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{s.logoutReason || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorAuthActivitySection;
